import { useEffect, useMemo, useState } from "react";
import {
  Input,
  Form,
  Modal,
  Typography,
  Flex,
  Space,
  Button,
  InputNumber,
  App,
  Checkbox,
} from "antd";

import { DeleteOutlined } from "@ant-design/icons";

import CustomSelect from "@/components/inputs/CustomSelect";

import {
  createFilter,
  deleteFilter,
  getAvailableFields,
} from "@/api/definitionFilters";

import { DEFINITIONS } from "@/constants/definitions";

const { Text } = Typography;

// Окно для создания/редактирования фильтра
const FilterModal = ({ data = {}, visible, onSave, onCancel }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [availableFields, setAvailableFields] = useState([]);

  const fieldValue = Form.useWatch("field_uuid", form);

  const showMultiControl = useMemo(() => {
    const field = availableFields.find((field) => field.uuid === fieldValue);

    return DEFINITIONS.includes(field?.type);
  }, [availableFields, fieldValue]);

  useEffect(() => {
    getAvailableFieldsList();
    // eslint-disable-next-line
  }, [data]);

  const getAvailableFieldsList = async () => {
    try {
      const fields = await getAvailableFields({
        definition_uuid: data.definition_uuid,
      });
      setAvailableFields(fields);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  const onValuesChange = (changedValues) => {
    if ("field_uuid" in changedValues) {
      const selectedField = availableFields.find(
        (field) => field.uuid === changedValues.field_uuid
      );
      if (selectedField) {
        form.setFieldsValue({
          caption: selectedField.name,
        });
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    try {
      await createFilter({
        ...data,
        ...values,
      });

      onSave();
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  const handleDelete = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить фильтр ${data.caption}?`,
      content: "Это действие нельзя будет отменить.",

      onOk() {
        handleConfirmDelete();
      },
      okText: "Удалить",
      cancelText: "Отмена",
      zIndex: 99999,
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteFilter({ uuid: data.uuid });
      onSave();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  return (
    <Modal
      maskClosable={false}
      title={
        data?.uuid ? "Редактирование фильтра" : "Добавление нового фильтра"
      }
      open={visible}
      onCancel={handleCancel}
      footer={
        <Flex align="center" justify="space-between">
          {data?.name && (
            <Typography.Link onClick={handleDelete} style={{ color: "red" }}>
              <Space>
                <DeleteOutlined /> Удалить фильтр
              </Space>
            </Typography.Link>
          )}
          <Space style={{ marginLeft: "auto" }}>
            <Button key="cancel" onClick={handleCancel}>
              Отмена
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={isLoading}
              onClick={onOk}
            >
              {data?.uuid ? "Сохранить" : "Добавить"}
            </Button>
          </Space>
        </Flex>
      }
      zIndex={10000}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...data,
          is_multiselect: data?.is_multiselect ?? true,
        }}
        onFinish={handleSave}
        onValuesChange={onValuesChange}
      >
        <Flex gap={20}>
          <Form.Item name="idx" label="№ п/п">
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="caption"
            label="Название фильтра"
            style={{ flex: 1 }}
          >
            <Input />
          </Form.Item>
        </Flex>
        <Form.Item
          name="field_uuid"
          label="Поле"
          rules={[
            {
              required: true,
              message: "Пожалуйста, выберите поле",
            },
          ]}
        >
          <CustomSelect
            placeholder="Начните вводить"
            options={availableFields}
            showSearch
            fieldNames={{ value: "uuid", label: "name" }}
            optionFilterProp="name"
          />
        </Form.Item>

        {showMultiControl && (
          <Form.Item name="is_multiselect" valuePropName="checked">
            <Checkbox defaultChecked="true">Множественный выбор</Checkbox>
          </Form.Item>
        )}

        {error && (
          <div>
            <Text type="danger">{error}</Text>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default FilterModal;
